"use client";

import Image from "next/image";
import { Book, Video, Users, Code, ArrowRight } from "lucide-react";
import { TrackedNavButton } from "@/components/ui/tracked-nav-button";
import { useButtonTracking } from "@/lib/useButtonTracking";
import { scrollToPricing } from "@/lib/utils";
import { useState } from "react";
import { useRouter } from "next/navigation";

interface LearningProps {
  isMobile: boolean;
}

export default function Learning({ isMobile }: LearningProps) {
  const { trackButtonClick } = useButtonTracking();
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const router = useRouter();

  const experts = [
    {
      image: "/3_learning/experts/garrett.jpeg",
      alt: "Garrett F",
      title: "How to build amazing UI",
      description:
        "47 mins fully packed with lots of tips & tricks: 1. How to leverage v0.dev/themes 2...",
    },
    {
      image: "/3_learning/experts/helicone.jpeg",
      alt: "Justin Torre",
      title: "How to prompt 10x better",
      description:
        "I had a pleasure to interview Justin Torre, Founder of Helicone (Leading open source...",
    },
    {
      image: "/3_learning/experts/sully.jpeg",
      alt: "Sully Omarr",
      title: "How to build research agent",
      description:
        "Podcast with Sully, creator of an amazing grid, where he shares his journey",
    },
    {
      image: "/3_learning/experts/bolt.jpeg",
      alt: "Bolt",
      title: "Podcast with Bolt.new CEO",
      description:
        "Deep dive into bolt.new epic launch, grew from 0 to 1 million + messages in 7 days",
    },
    {
      image: "/3_learning/experts/ethan.jpeg",
      alt: "Ethan Trang",
      title: "Podcast w/ Ethan Trang",
      description:
        "How a 19 yr old went from 0 coding knowledge to launching 2 AI products in 12...",
    },
  ];

  const handleCourseButtonClick = (courseName: string, buttonName: string) => {
    // Track the click for analytics
    trackButtonClick(`course_${courseName}_${buttonName}`, {
      section: 'learning',
      action: 'click',
      course: courseName,
      button_name: buttonName
    });
    
    // Use the router from component scope - no need to call useRouter() here
    router.push('/auth/signin');
  };

  const handlePrevCards = () => {
    setActiveCardIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextCards = () => {
    setActiveCardIndex((prev) =>
      Math.min(experts.length - (isMobile ? 1 : 4), prev + 1)
    );
  };

  return (
    <section id="courses" className="bg-black text-white pt-16">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <h2
            className={`${
              isMobile ? "text-5xl" : "text-6xl"
            } font-bold mb-6`}
          >
            Courses
          </h2>

          <div className="flex justify-between items-center mb-8">
            <p className={`${isMobile ? "text-xl" : "text-2xl"} text-gray-300`}>
              Learn from structured practical courses and expert interviews
            </p>
            
            <TrackedNavButton
              className="bg-stone-800 text-white hover:bg-stone-700 py-3 px-8 rounded-full font-medium transition-colors w-fit inline-flex items-center group text-base"
              onClick={() => {
                trackButtonClick('courses_page_view_all_top', {
                  section: 'learning',
                  action: 'click',
                  target: 'courses_page'
                });
                router.push('/courses');
              }}
            >
              <span>View all courses</span>
              <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
            </TrackedNavButton>
          </div>

          {/* Course Cards */}
          <div className={`space-y-8 ${isMobile ? "mb-8" : "mb-12"}`}>
            {/* First card - AI Coding Course */}
            <div className="bg-[#111111] border border-gray-800 p-0 overflow-hidden rounded-3xl shadow-lg">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <h3 className="text-5xl md:text-6xl font-bold mb-5">
                    AI coding
                    <br />
                    Beyond basics
                  </h3>
                  <p className="text-white/70 text-xl mb-6">
                    The ultimate Vibe Coding Guideline
                    <br />
                    to bring ideas to production
                  </p>
                  <TrackedNavButton
                    className="bg-white text-black hover:bg-white/90 py-3 px-8 rounded-full font-medium transition-colors mt-2 w-fit inline-flex items-center group text-base"
                    onClick={() => handleCourseButtonClick('ai_coding_beyond_basics', 'enroll_now')}
                  >
                    <span>Enroll now</span>
                    <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
                  </TrackedNavButton>
                </div>
                <div className="bg-[#111111] relative overflow-hidden min-h-[400px] md:min-h-[500px]">
                  <div className="w-full h-full">
                    <video
                      src="/3_learning/course1.mp4"
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    {/* Overlay to blend video with card background */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#111111]/90 pointer-events-none"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Second card - Build Production Ready Agent */}
            <div className="bg-[#111111] border border-gray-800 p-0 overflow-hidden rounded-3xl shadow-lg">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <h3 className="text-5xl md:text-6xl font-bold mb-5">
                    Build
                    <br />
                    Production
                    <br />
                    Agent
                  </h3>
                  <p className="text-white/70 text-xl mb-6">
                    No framework, just build from scratch
                  </p>
                  <TrackedNavButton
                    className="bg-white text-black hover:bg-white/90 py-3 px-8 rounded-full font-medium transition-colors mt-2 w-fit inline-flex items-center group text-base"
                    onClick={() => handleCourseButtonClick('build_production_agent', 'get_started')}
                  >
                    <span>Get Started</span>
                    <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
                  </TrackedNavButton>
                </div>
                <div className="bg-[#111111] relative overflow-hidden min-h-[400px] md:min-h-[500px]">
                  <div className="w-full h-full">
                    <video
                      src="/3_learning/course2.mp4"
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    {/* Overlay to blend video with card background */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#111111]/90 pointer-events-none"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Third card - Weekly Live Workshop */}
            <div className="bg-[#111111] border border-gray-800 p-0 overflow-hidden rounded-3xl shadow-lg">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <h3 className="text-5xl md:text-6xl font-bold mb-5">
                    Weekly Live
                    <br />
                    Workshop
                  </h3>
                  <p className="text-white/70 text-xl mb-6">
                    Most trending tools and projects.
                    <br />
                    Stay ahead, build faster
                  </p>
                  <TrackedNavButton
                    className="bg-white text-black hover:bg-white/90 py-3 px-8 rounded-full font-medium transition-colors mt-2 w-fit inline-flex items-center group text-base"
                    onClick={() => handleCourseButtonClick('weekly_live_workshop', 'join_now')}
                  >
                    <span>Join now</span>
                    <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
                  </TrackedNavButton>
                </div>
                <div className="bg-[#111111] relative overflow-hidden min-h-[400px] md:min-h-[500px]">
                  <div className="w-full h-full">
                    <video
                      src="/3_learning/course3.mp4"
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    {/* Overlay to blend video with card background */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#111111]/90 pointer-events-none"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Learn with industry experts */}
          <h2
            className={`${
              isMobile ? "text-xl" : "text-2xl"
            } font-bold mb-6`}
          >
            Learn with industry experts
          </h2>

          <div className="relative overflow-hidden">
            <div className="w-full overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{
                  transform: `translateX(-${
                    activeCardIndex * (isMobile ? 100 : 25)
                  }%)`,
                }}
              >
                {experts.map((expert, index) => (
                  <div
                    key={index}
                    className={`${
                      isMobile ? "w-full" : "w-full sm:w-1/2 lg:w-1/4"
                    } flex-shrink-0 px-2`}
                  >
                    <div className="bg-[#111111] rounded-lg overflow-hidden flex flex-col h-full relative">
                      {/* Image section with gradient shadow */}
                      <div className={`${isMobile ? "h-48" : "h-36"} relative`}>
                        <Image
                          src={expert.image}
                          alt={expert.alt}
                          fill
                          className="object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#111111] to-transparent opacity-70"></div>
                      </div>
                      <div className="p-4 flex flex-col flex-grow">
                        <h3
                          className={`${
                            isMobile ? "text-base" : "text-lg"
                          } mb-2 font-medium`}
                        >
                          {expert.title}
                        </h3>
                        <p
                          className={`${
                            isMobile ? "text-xs" : "text-sm"
                          } text-white/50 mb-2`}
                        >
                          {expert.description}
                        </p>
                        <div className="mt-auto">
                          <div className="w-full bg-gray-800 h-1 rounded-full">
                            <div className="bg-white h-1 rounded-full w-[0%]"></div>
                          </div>
                          <div
                            className={`${
                              isMobile ? "text-xs" : "text-sm"
                            } text-gray-400 mt-1`}
                          >
                            0%
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation buttons and View all courses button */}
          <div className="flex justify-between items-center mt-4 px-2">
            <TrackedNavButton
              className="bg-stone-800 text-white hover:bg-stone-700 py-3 px-8 rounded-full font-medium transition-colors w-fit inline-flex items-center group text-base"
              onClick={() => {
                trackButtonClick('courses_page_view_all_bottom', {
                  section: 'learning',
                  action: 'click',
                  target: 'courses_page'
                });
                router.push('/courses');
              }}
            >
              <span>View all courses</span>
              <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
            </TrackedNavButton>
            
            <div className="flex gap-2">
              <button
                className={`p-2 ${
                  activeCardIndex > 0
                    ? "bg-stone-800"
                    : "bg-stone-950 text-gray-600"
                } rounded-full`}
                onClick={handlePrevCards}
                disabled={activeCardIndex === 0}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>
              <button
                className={`p-2 ${
                  activeCardIndex < experts.length - (isMobile ? 1 : 4)
                    ? "bg-stone-800"
                    : "bg-stone-950 text-gray-600"
                } rounded-full`}
                onClick={handleNextCards}
                disabled={activeCardIndex >= experts.length - (isMobile ? 1 : 4)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
